import {getMeasurementUnitLabel} from '../../screens/FormBuilderScreen/shared';

const EMPTY_ARRAY = [];

// Whether a poll left the widget's submissions as they were, so the list can keep its rows and the reader's scroll
// position.
export function isSameSubmissions(previous, next) {
  if (!previous) {
    return false;
  }

  const previousIds = Object.keys(previous);
  const nextIds = Object.keys(next);

  return previousIds.length === nextIds.length
    && nextIds.every(rowId => isSameSubmission(previous[rowId], next[rowId]));
}

// An edit leaves a row's last modified date where it was, so the answers are compared too. All of the row's answers,
// not only the one shown: the widget's filter reads the others, and an edit to one of them can move the row in or out.
function isSameSubmission(previous, next) {
  if (previous === next) {
    return true;
  }

  return Boolean(previous)
    && previous.lastModified === next.lastModified
    && getAnswersKey(previous.rowAnswers) === getAnswersKey(next.rowAnswers);
}

// The API holds a row's answers as a set, so the same answers can come back in a different order.
function getAnswersKey(answers) {
  return JSON.stringify((answers || EMPTY_ARRAY)
    .map(answer => [answer.id, answer.questionId, answer.value])
    .sort((a, b) => String(a[0]).localeCompare(String(b[0]))));
}

// Adds what a poll fetched - submissions by row id, null for a row that does not answer the question - to what earlier
// polls did. Most polls fetch what we already have, and returning the same object then saves working the totals out
// again.
export function addPolled(previous, fetched) {
  const unchanged = Boolean(previous) && Object.keys(fetched).every(rowId => rowId in previous && (
    previous[rowId] === fetched[rowId] || (Boolean(fetched[rowId]) && isSameSubmission(previous[rowId], fetched[rowId]))
  ));

  return unchanged ? previous : {...previous, ...fetched};
}

// Merges the submissions our polls have fetched into the ones handed down by the dashboard, for working out the
// widget's totals. The dashboard loads every row of the sheets, so between the two this is every submission there is,
// not just the ones the list keeps. A polled submission replaces the handed down one with the same row id, since it is
// the newer of the two, and is dropped when the widget's filter no longer lets it through. A row polled as null no
// longer answers the question and is dropped too. The handed down submissions have already been filtered.
export function mergeSubmissions(seedSubmissions, polledById, matchesFilter) {
  if (!polledById) {
    return seedSubmissions;
  }

  const kept = seedSubmissions.filter(submission => !(submission.rowId in polledById));
  const polled = Object.keys(polledById)
    .map(rowId => polledById[rowId])
    .filter(submission => submission && matchesFilter(submission));

  return kept.concat(polled);
}

// The question types whose answers add up to a total.
const TOTALLED_TYPES = ['NUMERIC', 'MEASURED_NUMERIC'];

/**
 * How many submissions there are and, for a number or measured number question, what their answers add up to and in
 * what unit. The total is null for any other question. Answers that are not a number are left out of the total.
 */
export function tallySubmissions(submissions, question) {
  const count = submissions.length;

  if (!question || !TOTALLED_TYPES.includes(question.type)) {
    return {count, total: null, unit: null};
  }

  let total = 0;

  submissions.forEach(submission => {
    submission.answers.forEach(answer => {
      const value = Number(answer.value);

      if (answer.value === '' || answer.value === null || !isFinite(value)) {
        return;
      }

      total += value;
    });
  });

  return {count, total, unit: getMeasurementUnitLabel(question)};
}

// A count or total in full, with thousands separators, rather than abbreviated the way formatNumber does. Adding up
// decimals leaves floating point noise, so at most two decimal places are kept.
export function formatTally(value, locale) {
  return value.toLocaleString(locale, {maximumFractionDigits: 2});
}
